import {
  CheckOutlined,
  MailOutlined,
  PhoneOutlined,
  ReloadOutlined,
  UserOutlined,
} from '@ant-design/icons';
import {
  Alert,
  Button,
  Card,
  Descriptions,
  message,
  Popconfirm,
  Space,
  Spin,
  Typography,
} from 'antd';
import type React from 'react';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { raffleErrorKey, raffleGet, rafflePost } from '../config/raffleApi';

const { Title, Text } = Typography;

/**
 * The one screen in the whole product that shows a real identity (#89 point 5).
 *
 * Everywhere else — the list of entries, the winner history, the Stage Screen,
 * every socket payload — a participant is only ever their generated pseudonym.
 * The name, e-mail and phone behind it come from a single endpoint,
 * `GET /raffle/draws/:drawId/winners/:winnerId`, and this component is its only
 * caller. It is fetched when a drawn winner is actually waiting to be
 * confirmed, and not a moment earlier: the panel is often mirrored on a second
 * screen, so an identity that loads "just in case" is an identity on a wall.
 */

/** The identity response. Present only on this endpoint. */
interface RaffleWinnerIdentity {
  id: string;
  playerName: string;
  status: 'DRAWN' | 'CONFIRMED' | 'NO_RESPONSE';
  drawnAt: string;
  confirmedAt?: string | null;
  prize: {
    id: string;
    name: string;
  };
  participant: {
    name: string;
    email?: string | null;
    phone?: string | null;
  };
}

interface RaffleWinnerPanelProps {
  drawId: string;
  /** The `DRAWN` winner awaiting a decision. */
  winnerId: string;
  /** The pseudonym already known from the list — shown while the identity loads. */
  playerName: string;
  /**
   * The round is over. Both decisions need a DRAWING round on the server, so
   * offering them here could only produce an error message (#174).
   */
  roundClosed?: boolean;
  /** Re-read the round after confirm or redraw. */
  onResolved: () => void;
}

const RaffleWinnerPanel: React.FC<RaffleWinnerPanelProps> = ({
  drawId,
  winnerId,
  playerName,
  roundClosed = false,
  onResolved,
}) => {
  const { t } = useTranslation();
  const [identity, setIdentity] = useState<RaffleWinnerIdentity | null>(null);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);
  const [acting, setActing] = useState(false);

  const fetchIdentity = useCallback(async () => {
    setLoading(true);
    setFailed(false);
    try {
      const data = await raffleGet<RaffleWinnerIdentity>(
        `/raffle/draws/${drawId}/winners/${winnerId}`
      );
      setIdentity(data);
    } catch (error) {
      setFailed(true);
      message.error(t(raffleErrorKey(error)));
    } finally {
      setLoading(false);
    }
  }, [drawId, winnerId, t]);

  useEffect(() => {
    // Keyed on `winnerId`, so a redraw — which writes a NEW winner row rather
    // than overwriting the old one — refetches instead of showing the previous
    // person's contact details next to the new pseudonym.
    setIdentity(null);
    fetchIdentity();
  }, [fetchIdentity]);

  const handleConfirm = async () => {
    setActing(true);
    try {
      await rafflePost(`/raffle/draws/${drawId}/winners/${winnerId}/confirm`);
      message.success(t('raffle.winner.confirmed'));
      onResolved();
    } catch (error) {
      message.error(t(raffleErrorKey(error)));
    } finally {
      setActing(false);
    }
  };

  const handleRedraw = async () => {
    setActing(true);
    try {
      await rafflePost(`/raffle/draws/${drawId}/winners/${winnerId}/redraw`);
      message.success(t('raffle.winner.redrawn'));
      onResolved();
    } catch (error) {
      message.error(t(raffleErrorKey(error)));
    } finally {
      setActing(false);
    }
  };

  const participant = identity?.participant;
  const isEmail = Boolean(participant?.email);

  return (
    <Card title={t('raffle.winner.title')} styles={{ header: { background: '#fff7e6' } }}>
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        <div>
          <Text type="secondary">{t('raffle.winner.playerName')}</Text>
          <Title level={2} style={{ margin: '4px 0 0', letterSpacing: 1 }}>
            {identity?.playerName || playerName}
          </Title>
          {identity?.prize?.name && <Text type="secondary">{identity.prize.name}</Text>}
        </div>

        {loading && <Spin />}

        {failed && !loading && (
          <Alert
            type="error"
            showIcon
            message={t('raffle.winner.identityFailed')}
            action={
              <Button size="small" onClick={fetchIdentity}>
                {t('raffle.winner.retry')}
              </Button>
            }
          />
        )}

        {participant && (
          <Descriptions column={1} bordered size="small">
            <Descriptions.Item
              label={
                <Space>
                  <UserOutlined />
                  {t('raffle.winner.realName')}
                </Space>
              }
            >
              <Text strong style={{ fontSize: 16 }}>
                {participant.name}
              </Text>
            </Descriptions.Item>
            <Descriptions.Item
              label={
                <Space>
                  {isEmail ? <MailOutlined /> : <PhoneOutlined />}
                  {isEmail ? t('raffle.winner.email') : t('raffle.winner.phone')}
                </Space>
              }
            >
              <Text copyable style={{ fontSize: 16 }}>
                {participant.email || participant.phone || '-'}
              </Text>
            </Descriptions.Item>
          </Descriptions>
        )}

        {roundClosed ? (
          <Alert type="warning" showIcon message={t('raffle.winner.roundClosed')} />
        ) : (
          <Space size="middle" wrap>
            <Button
              type="primary"
              size="large"
              icon={<CheckOutlined />}
              loading={acting}
              onClick={handleConfirm}
            >
              {t('raffle.actions.confirmWinner')}
            </Button>
            {/* A redraw is irreversible: it marks this person NO_RESPONSE and
              pulls somebody else. It never happens without a second press. */}
            <Popconfirm
              title={t('raffle.winner.confirmRedrawTitle')}
              description={t('raffle.winner.confirmRedrawBody')}
              onConfirm={handleRedraw}
              okText={t('common.yes')}
              cancelText={t('common.no')}
            >
              <Button size="large" danger icon={<ReloadOutlined />} loading={acting}>
                {t('raffle.actions.redraw')}
              </Button>
            </Popconfirm>
          </Space>
        )}
      </Space>
    </Card>
  );
};

export default RaffleWinnerPanel;
